import { useTranslation } from "react-i18next";
import type { ArtifactContent } from "../../../api/content/authoring/processContentDecoders";
import MarkdownRenderer from "../../../ui/content/MarkdownRenderer";
import { annotationSetOf, checklistRows, editorOf, glossaryRows, wordingsText } from "../../processAuthoring/artifacts/artifactTables";
import AnnotationSetView from "../../annotation/AnnotationSetView";
import { readableJsonText } from "../../../shared/formatting/readableJson";
import SourceView from "../../../ui/content/source/SourceView";

const CHECKLIST_COLUMNS = ["id", "requirement", "reference", "part_type", "labels", "xbrl_concept"] as const;

/** An artifact's content as people read it: Markdown rendered, known schemas as tables, an annotation set in the span view, other JSON as stored. */
export default function ArtifactContentView({ artifact }: { artifact: ArtifactContent }) {
  const { t } = useTranslation();
  const editor = editorOf(artifact.media_type, artifact.schema);
  if (editor === "markdown" && typeof artifact.content === "string") {
    return <MarkdownRenderer content={artifact.content} className="app-markdown max-h-[32rem] overflow-auto text-sm" />;
  }
  const glossary = editor === "glossary" ? glossaryRows(artifact.content) : null;
  if (glossary !== null) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <caption className="app-text-muted text-left">{t("authoring.artifactPage.terms", { count: glossary.length })}</caption>
          <thead>
            <tr className="app-text-muted text-left">
              <th className="px-1">{t("authoring.artifact.glossary.key")}</th>
              <th className="px-1">{t("authoring.artifact.glossary.label")}</th>
              <th className="px-1">{t("authoring.artifact.glossary.de")}</th>
              <th className="px-1">{t("authoring.artifact.glossary.en")}</th>
              <th className="px-1">{t("authoring.artifact.glossary.note")}</th>
            </tr>
          </thead>
          <tbody>
            {glossary.map((row, index) => (
              <tr key={index} className="align-top">
                <td className="px-1 font-mono">{row.key}</td>
                <td className="px-1 font-mono">{row.label}</td>
                <td className="px-1">{wordingsText(row.de)}</td>
                <td className="px-1">{wordingsText(row.en)}</td>
                <td className="px-1">{row.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  const annotationSet = editor === "annotations" ? annotationSetOf(artifact.content) : null;
  if (annotationSet !== null) {
    return <AnnotationSetView output={annotationSet} />;
  }
  const checklist = editor === "checklist" ? checklistRows(artifact.content) : null;
  if (checklist !== null) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <caption className="app-text-muted text-left">{t("authoring.artifactPage.items", { count: checklist.length })}</caption>
          <thead>
            <tr className="app-text-muted text-left">
              {CHECKLIST_COLUMNS.map((column) => (
                <th key={column} className="px-1">{t(`authoring.artifact.checklist.columns.${column}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {checklist.map((row, index) => (
              <tr key={index} className="align-top">
                <td className="px-1 font-mono">{row.id}</td>
                <td className="px-1">{row.requirement}</td>
                <td className="px-1">{row.reference}</td>
                <td className="px-1">{row.part_type}</td>
                <td className="px-1 font-mono">{wordingsText(row.labels)}</td>
                <td className="px-1 font-mono">{row.xbrl_concept}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <SourceView label={t("authoring.artifact.json")} language="json" value={readableJsonText(artifact.content)} />
  );
}
